import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeafletFloorMap from "../../components/LeafletFloorMap";
import QrScannerModal from "../../components/QrScannerModal";

import {
  NavigationAPI,
  NavigationGraphAPI,
  PathClosureAPI,
  ShapeAPI,
  DestinationAPI,
  FloorAPI,
  BuildingAPI,
  resolveAsset,
} from "../../services/api";
import { parseNames, translateText } from "../../utils/nameParser";
import useDeviceHeading from "../../hooks/useDeviceHeading";
import { normalizeDegrees } from "../../utils/routeProgress";
import finishIconUrl from "../../assets/icons/navigation/finish.svg";
import { ReactComponent as QrcodeIcon } from "../../assets/icons/user2/qrcode.svg";

const denorm = (v, scale) => (v <= 1 ? v * scale : v);

function pointToSegmentDist(px, py, ax, ay, bx, by) {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

function isSegmentBlocked(a, b, closures, imgW, imgH) {
  for (const c of closures) {
    const cx = denorm(c.x, imgW);
    const cy = denorm(c.y, imgH);
    const r = c.radius_px || c.radiusPx || 80;
    const d = pointToSegmentDist(cx, cy, a.x, a.y, b.x, b.y);
    if (d <= r) return true;
  }
  return false;
}

function isPathBlocked(path, closures, imgW, imgH) {
  if (!Array.isArray(path) || path.length < 2) return false;
  for (let i = 0; i < path.length - 1; i += 1) {
    if (isSegmentBlocked(path[i], path[i + 1], closures, imgW, imgH)) return true;
  }
  return false;
}

function getShapeCenter(shape, imgW = 1000, imgH = 700) {
  if (Array.isArray(shape.points) && shape.points.length > 0) {
    const sumX = shape.points.reduce((sum, p) => sum + denorm(p.x, imgW), 0);
    const sumY = shape.points.reduce((sum, p) => sum + denorm(p.y, imgH), 0);
    return { x: sumX / shape.points.length, y: sumY / shape.points.length };
  }
  const x = denorm(shape.x || 0, imgW);
  const y = denorm(shape.y || 0, imgH);
  const w = denorm(shape.width || 0, imgW);
  const h = denorm(shape.height || 0, imgH);
  return { x: x + w / 2, y: y + h / 2 };
}

const shapeBelongsToFloor = (shape, floorId) => {
  const shapeFloorId = shape.floorId ?? shape.floor_id ?? shape.floor?.id;
  return shapeFloorId == null || String(shapeFloorId) === String(floorId);
};

function getShapeBoundaryPoints(shape, imgW = 1000, imgH = 700) {
  if (Array.isArray(shape.points) && shape.points.length > 1) {
    return shape.points.map((point) => ({
      x: denorm(point.x, imgW),
      y: denorm(point.y, imgH),
    }));
  }
  const x = denorm(shape.x || 0, imgW);
  const y = denorm(shape.y || 0, imgH);
  const w = denorm(shape.width || 0, imgW);
  const h = denorm(shape.height || 0, imgH);
  if (shape.type === "circle") {
    const cx = x + w / 2, cy = y + h / 2, rx = w / 2, ry = h / 2;
    return Array.from({ length: 32 }, (_, index) => {
      const angle = (Math.PI * 2 * index) / 32;
      return { x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry };
    });
  }
  if (shape.type === "triangle") return [{ x: x + w / 2, y }, { x, y: y + h }, { x: x + w, y: y + h }];
  return [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
}

function shapeBoundarySegments(shape, imgW = 1000, imgH = 700) {
  const points = getShapeBoundaryPoints(shape, imgW, imgH);
  if (points.length < 2) return [];
  return points.map((point, index) => ({ a: point, b: points[(index + 1) % points.length] }));
}

function readPoint(searchParams, prefix) {
  const x = Number(searchParams.get(`${prefix}X`) ?? searchParams.get(`${prefix}x`));
  const y = Number(searchParams.get(`${prefix}Y`) ?? searchParams.get(`${prefix}y`));
  return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

function normalizeCorridors(data) {
  const raw =
    (Array.isArray(data) && data) ||
    data?.items || data?.corridors ||
    data?.data?.items || data?.data?.corridors || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((corridor) => {
      const points = corridor.points || corridor.path || corridor.polyline || [];
      return {
        id: corridor.id,
        points: Array.isArray(points)
          ? points
            .map((point) => ({ x: Number(point?.x ?? point?.lng), y: Number(point?.y ?? point?.lat) }))
            .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y))
          : [],
      };
    })
    .filter((corridor) => corridor.points.length >= 2);
}

const distanceBetween = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const WALKWAY_SNAP_THRESHOLD = 36;
const BRIDGE_ARRIVAL_THRESHOLD = 48;
const MIN_STEP_DISTANCE = 12;
const TURN_ANGLE_THRESHOLD = 30;

function normalizeFloorName(value) {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .toLowerCase()
    .replace(/floor|ชั้น|ชั้นที่/g, "")
    .replace(/[^\p{L}\p{N}]+/gu, "");
}

function floorComparableValue(floor) {
  const level = Number(floor?.level ?? floor?.floorLevel);
  if (Number.isFinite(level)) return `level:${level}`;
  const name = normalizeFloorName(floor?.name || floor?.floorName);
  return name ? `name:${name}` : "";
}

function isDifferentPhysicalFloor(currentFloor, targetFloor, currentFloorId, targetFloorId) {
  if (String(currentFloorId) === String(targetFloorId)) return false;
  const currentComparable = floorComparableValue(currentFloor);
  const targetComparable = floorComparableValue(targetFloor);
  if (currentComparable && targetComparable) return currentComparable !== targetComparable;
  return true;
}

function pointKey(point) {
  return `${Math.round(point.x * 100) / 100},${Math.round(point.y * 100) / 100}`;
}

function projectPointToSegment(point, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (!lenSq) return { point: a, t: 0, distance: distanceBetween(point, a) };
  const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / lenSq));
  const projected = { x: a.x + dx * t, y: a.y + dy * t };
  return { point: projected, t, distance: distanceBetween(point, projected) };
}

function angleBetweenSegments(a, b, c) {
  const v1 = { x: b.x - a.x, y: b.y - a.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const len1 = Math.hypot(v1.x, v1.y), len2 = Math.hypot(v2.x, v2.y);
  if (!len1 || !len2) return 0;
  const dot = v1.x * v2.x + v1.y * v2.y;
  const cross = v1.x * v2.y - v1.y * v2.x;
  return (Math.atan2(cross, dot) * 180) / Math.PI;
}

function simplifyInstructionPath(path) {
  return path.reduce((points, point) => {
    const previous = points[points.length - 1];
    if (!previous || distanceBetween(previous, point) >= MIN_STEP_DISTANCE) points.push(point);
    return points;
  }, []);
}

function buildTurnSteps(path, t, { includeArrival = true, scale = 0.05 } = {}) {
  const points = simplifyInstructionPath(path);
  if (points.length < 2) {
    return [{ type: "straight", text: t("user.navigation.followPath"), pointIndex: 0, point: points[0] }];
  }

  const steps = [
    { type: "straight", text: t("user.navigation.goStraight"), pointIndex: 0, point: points[0] },
  ];

  for (let i = 1; i < points.length - 1; i += 1) {
    const angle = angleBetweenSegments(points[i - 1], points[i], points[i + 1]);

    if (Math.abs(angle) < TURN_ANGLE_THRESHOLD) {
      steps.push({ type: "straight", text: t("user.navigation.continueStraight"), pointIndex: i, point: points[i] });
    } else if (angle > 0) {
      steps.push({ type: "right", text: t("user.navigation.turnRight"), pointIndex: i, point: points[i] });
    } else {
      steps.push({ type: "left", text: t("user.navigation.turnLeft"), pointIndex: i, point: points[i] });
    }
  }

  if (includeArrival) {
    steps.push({ type: "arrive", text: t("user.navigation.arriveDestination"), pointIndex: points.length - 1, point: points[points.length - 1] });
  }

  const unit = t("user.navigation.distance") === "Distance" ? "m" : "เมตร";

  for (let i = 0; i < steps.length; i += 1) {
    const startIdx = steps[i].pointIndex;
    let endIdx = null;

    if (i < steps.length - 1) {
      endIdx = steps[i + 1].pointIndex;
    } else if (!includeArrival) {
      endIdx = points.length - 1;
    }

    if (endIdx !== null && endIdx > startIdx) {
      let dist = 0;
      for (let k = startIdx; k < endIdx; k += 1) {
        dist += distanceBetween(points[k], points[k + 1]);
      }
      const distanceMeters = Math.max(1, Math.round(dist * scale));
      steps[i].text = `${steps[i].text} ${distanceMeters} ${unit}`; // distance text virable
    }
  }

  return steps;
}

function getStepIcon(type) {
  switch (type) {
    case "left":
      return "←";
    case "right":
      return "→";
    case "arrive":
      return "★";
    case "transfer":
      return "⇅";
    case "straight":
    default:
      return "↑";
  }
}

function StepCardIcon({ type }) {
  if (type === "arrive") {
    return (
      <span
        className="block h-7 w-7 bg-white"
        style={{
          WebkitMask: `url(${finishIconUrl}) center / contain no-repeat`,
          mask: `url(${finishIconUrl}) center / contain no-repeat`,
        }}
      />
    );
  }

  return getStepIcon(type);
}

function normalizeConnectorName(value) {
  return String(value || "")
    .normalize("NFKC").trim().toLowerCase()
    .replace(/ลิฟท์|ลิฟต์|elevator|lift/g, "lift")
    .replace(/[^\p{L}\p{N}]+/gu, "")
    .replace(/\s+/g, "");
}

function elevatorLabel(point) {
  return point?.connectorCode || point?.name || point?.text || point?.label || point?.title || "";
}

function isElevatorShape(shape) {
  const category = String(shape?.category || shape?.type || "").trim().toLowerCase();
  return category === "elevator" || category === "lift";
}

function isStairShape(shape) {
  const category = String(shape?.category || shape?.type || "").trim().toLowerCase();
  return category === "stair" || category === "stairs";
}

function isBridgeShape(shape) {
  const category = String(shape?.category || shape?.type || "").trim().toLowerCase();
  return category === "bridge";
}

function bridgeShapeToPoint(shape, floorData) {
  const center = getShapeCenter(shape, floorData?.imageWidth || 1000, floorData?.imageHeight || 700);
  return {
    ...shape,
    id: `shape-bridge-${shape.id}`,
    shapeId: shape.id,
    x: Math.round(center.x),
    y: Math.round(center.y),
    name: shape.name || shape.text || shape.label,
  };
}

function getFloorBridges(floorData, shapes) {
  return (Array.isArray(shapes) ? shapes : [])
    .filter((shape) => !shape.hidden && isBridgeShape(shape))
    .map((shape) => bridgeShapeToPoint(shape, floorData));
}

function normalizeListResponse(data, key) {
  return (Array.isArray(data) && data) || data?.items || data?.[key] || data?.data?.items || data?.data?.[key] || [];
}

function mergeUniqueById(items) {
  return Array.from(
    new Map(
      items
        .filter((item) => item?.id != null)
        .map((item) => [String(item.id), item])
    ).values()
  );
}

async function loadAllBuildingsForBridgeGraph() {
  const firstRequests = await Promise.all([
    BuildingAPI.list().catch(() => null),
    BuildingAPI.list({ page: 1, limit: 1000 }).catch(() => null),
  ]);

  let buildings = mergeUniqueById(
    firstRequests.flatMap((data) => normalizeListResponse(data, "buildings"))
  );

  const pagedResponse = firstRequests.find((data) => data && !Array.isArray(data) && (data.total || data.totalPages));
  const total = Number(pagedResponse?.total ?? pagedResponse?.data?.total);
  const pageSize = Number(
    pagedResponse?.pageSize ??
    pagedResponse?.limit ??
    pagedResponse?.data?.pageSize ??
    pagedResponse?.data?.limit ??
    buildings.length
  );
  const totalPages = Number(
    pagedResponse?.totalPages ??
    pagedResponse?.data?.totalPages ??
    (Number.isFinite(total) && Number.isFinite(pageSize) && pageSize > 0 ? Math.ceil(total / pageSize) : 1)
  );

  if (Number.isFinite(totalPages) && totalPages > 1 && totalPages <= 50) {
    const rest = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        BuildingAPI.list({ page: index + 2, limit: pageSize || 100 }).catch(() => null)
      )
    );
    buildings = mergeUniqueById([
      ...buildings,
      ...rest.flatMap((data) => normalizeListResponse(data, "buildings")),
    ]);
  }

  return buildings;
}

function mergeFloorNavigationData(navigationData, floorMeta) {
  if (!navigationData && !floorMeta) return null;
  return {
    ...(floorMeta || {}),
    ...(navigationData || {}),
    id: navigationData?.id ?? floorMeta?.id,
    name: navigationData?.name ?? floorMeta?.name,
    level: navigationData?.level ?? floorMeta?.level,
    floorLevel: navigationData?.floorLevel ?? floorMeta?.floorLevel ?? floorMeta?.level,
  };
}

async function loadFloorNavigationData(floorId) {
  const [navigationData, floorMeta] = await Promise.all([
    NavigationAPI.getFloor(floorId).catch(() => null),
    FloorAPI.get(floorId).catch(() => null),
  ]);
  return mergeFloorNavigationData(navigationData, floorMeta);
}

function bridgeNameKey(bridge) {
  return normalizeConnectorName(elevatorLabel(bridge));
}

function bridgeMatchesPreferredFloor(bridge, preferredFloor, preferredFloorId) {
  if (!bridge) return false;
  if (String(bridge.floorId) === String(preferredFloorId)) return true;
  const bridgeComparable = floorComparableValue(bridge);
  const preferredComparable = floorComparableValue(preferredFloor);
  return !!bridgeComparable && bridgeComparable === preferredComparable;
}

function findBridgeRouteHop(
  bridgesByBuilding,
  currentBuildingId,
  finalBuildingId,
  start,
  floorId,
  preferredFloor,
  usedBridgeNames = new Set()
) {
  const ids = Array.from(bridgesByBuilding.keys());
  const adjacency = new Map(ids.map((id) => [String(id), []]));

  for (let i = 0; i < ids.length; i += 1) {
    for (let j = i + 1; j < ids.length; j += 1) {
      const aId = String(ids[i]);
      const bId = String(ids[j]);
      const aBridges = bridgesByBuilding.get(aId) || [];
      const bBridges = bridgesByBuilding.get(bId) || [];

      aBridges.forEach((aBridge) => {
        const aName = bridgeNameKey(aBridge);
        if (!aName) return;
        const bBridge = bBridges.find((bridge) => bridgeNameKey(bridge) === aName);
        if (!bBridge) return;
        adjacency.get(aId)?.push({ to: bId, currentBridge: aBridge, targetBridge: bBridge, name: aName });
        adjacency.get(bId)?.push({ to: aId, currentBridge: bBridge, targetBridge: aBridge, name: aName });
      });
    }
  }

  const preferredStartName = start
    ? (bridgesByBuilding.get(String(currentBuildingId)) || [])
        .find(
          (bridge) =>
            String(bridge.floorId) === String(floorId) &&
            distanceBetween(start, bridge) <= BRIDGE_ARRIVAL_THRESHOLD
        )
    : null;
  const preferredName = bridgeNameKey(preferredStartName);
  const sortEdges = (edges) =>
    [...edges].sort((a, b) => {
      const hasUnusedChoice = edges.some((edge) => !usedBridgeNames.has(edge.name));
      if (hasUnusedChoice) {
        if (!usedBridgeNames.has(a.name) && usedBridgeNames.has(b.name)) return -1;
        if (usedBridgeNames.has(a.name) && !usedBridgeNames.has(b.name)) return 1;
      }
      const hasSameFloorChoice = edges.some((edge) =>
        bridgeMatchesPreferredFloor(edge.currentBridge, preferredFloor, floorId)
      );
      if (hasSameFloorChoice) {
        const aSameFloor = bridgeMatchesPreferredFloor(a.currentBridge, preferredFloor, floorId);
        const bSameFloor = bridgeMatchesPreferredFloor(b.currentBridge, preferredFloor, floorId);
        if (aSameFloor && !bSameFloor) return -1;
        if (!aSameFloor && bSameFloor) return 1;
      }
      if (!preferredName) return 0;
      const hasForwardChoice = edges.some((edge) => edge.name !== preferredName);
      if (!hasForwardChoice) return 0;
      if (a.name !== preferredName && b.name === preferredName) return -1;
      if (a.name === preferredName && b.name !== preferredName) return 1;
      return 0;
    });

  const queue = [{ buildingId: String(currentBuildingId), firstHop: null }];
  const visited = new Set([String(currentBuildingId)]);

  while (queue.length) {
    const item = queue.shift();
    const edges = sortEdges(adjacency.get(item.buildingId) || []);
    for (const edge of edges) {
      if (visited.has(edge.to)) continue;
      const firstHop = item.firstHop || edge;
      if (edge.to === String(finalBuildingId)) return firstHop;
      visited.add(edge.to);
      queue.push({ buildingId: edge.to, firstHop });
    }
  }

  return null;
}

function elevatorShapeToPoint(shape, floorData) {
  const center = getShapeCenter(shape, floorData?.imageWidth || 1000, floorData?.imageHeight || 700);
  return { ...shape, id: `shape-elevator-${shape.id}`, x: Math.round(center.x), y: Math.round(center.y), name: shape.name || shape.text || shape.label };
}

function stairShapeToPoint(shape, floorData) {
  const center = getShapeCenter(shape, floorData?.imageWidth || 1000, floorData?.imageHeight || 700);
  return {
    ...shape,
    id: `shape-stair-${shape.id}`,
    x: Math.round(center.x),
    y: Math.round(center.y),
    name: shape.name || shape.text || shape.label,
  };
}

function getFloorElevators(floorData, shapes) {
  const apiElevators = Array.isArray(floorData?.elevators) ? floorData.elevators : [];
  const shapeElevators = (Array.isArray(shapes) ? shapes : [])
    .filter((shape) => !shape.hidden && isElevatorShape(shape))
    .map((shape) => elevatorShapeToPoint(shape, floorData));
  return [...apiElevators, ...shapeElevators];
}

function getFloorStairs(floorData, shapes) {
  const apiStairs = Array.isArray(floorData?.stairs) ? floorData.stairs : [];
  const shapeStairs = (Array.isArray(shapes) ? shapes : [])
    .filter((shape) => !shape.hidden && isStairShape(shape))
    .map((shape) => stairShapeToPoint(shape, floorData));
  return [...apiStairs, ...shapeStairs];
}

function routeDistance(path) {
  return path.reduce((sum, point, index) => {
    if (index === 0) return sum;
    return sum + distanceBetween(path[index - 1], point);
  }, 0);
}

function findBestElevatorTransfer(corridors, start, currentFloorData, targetFloorData, currentShapes, targetShapes, closures = []) {
  const currentElevators = getFloorElevators(currentFloorData, currentShapes);
  const targetElevators = getFloorElevators(targetFloorData, targetShapes);
  let best = null;
  currentElevators.forEach((currentElevator) => {
    const currentName = normalizeConnectorName(elevatorLabel(currentElevator));
    if (!currentName) return;
    const targetElevator = targetElevators.find(
      (candidate) => normalizeConnectorName(elevatorLabel(candidate)) === currentName
    );
    if (!targetElevator) return;
    const path = shortestCorridorPath(
      corridors,
      start,
      { x: Number(currentElevator.x), y: Number(currentElevator.y) },
      closures,
      currentFloorData?.imageWidth || 1000,
      currentFloorData?.imageHeight || 700
    );
    if (path.length < 2) return;
    const distance = routeDistance(path);
    if (!best || distance < best.distance) {
      best = { currentElevator, targetElevator, elevatorName: elevatorLabel(currentElevator), path, distance };
    }
  });
  return best;
}

function connectorLabel(point) {
  return point?.connectorCode || point?.name || point?.text || point?.label || point?.title || "";
}

function findBestConnectorTransfer(corridors, start, currentFloorData, targetFloorData, currentShapes, targetShapes, mode, closures = []) {
  if (mode !== "stair") {
    const elevatorTransfer = findBestElevatorTransfer(
      corridors,
      start,
      currentFloorData,
      targetFloorData,
      currentShapes,
      targetShapes,
      closures
    );
    return elevatorTransfer
      ? {
        currentConnector: elevatorTransfer.currentElevator,
        targetConnector: elevatorTransfer.targetElevator,
        connectorName: elevatorTransfer.elevatorName,
        path: elevatorTransfer.path,
        distance: elevatorTransfer.distance,
      }
      : null;
  }

  const currentConnectors =
    mode === "stair"
      ? getFloorStairs(currentFloorData, currentShapes)
      : getFloorElevators(currentFloorData, currentShapes);
  const targetConnectors =
    mode === "stair"
      ? getFloorStairs(targetFloorData, targetShapes)
      : getFloorElevators(targetFloorData, targetShapes);
  let best = null;

  currentConnectors.forEach((currentConnector, index) => {
    const currentName = normalizeConnectorName(connectorLabel(currentConnector));
    const targetConnector = currentName
      ? targetConnectors.find(
        (candidate) => normalizeConnectorName(connectorLabel(candidate)) === currentName
      )
      : targetConnectors[index] || targetConnectors[0];
    if (!targetConnector) return;

    const path = shortestCorridorPath(
      corridors,
      start,
      {
        x: Number(currentConnector.x),
        y: Number(currentConnector.y),
      },
      closures,
      currentFloorData?.imageWidth || 1000,
      currentFloorData?.imageHeight || 700
    );
    if (path.length < 2) return;

    const distance = routeDistance(path);
    if (!best || distance < best.distance) {
      best = {
        currentConnector,
        targetConnector,
        connectorName: connectorLabel(currentConnector),
        path,
        distance,
      };
    }
  });

  return best;
}

function segmentIntersection(a, b, c, d) {
  const r = { x: b.x - a.x, y: b.y - a.y };
  const s = { x: d.x - c.x, y: d.y - c.y };
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < 0.000001) return null;
  const qmp = { x: c.x - a.x, y: c.y - a.y };
  const t = (qmp.x * s.y - qmp.y * s.x) / denom;
  const u = (qmp.x * r.y - qmp.y * r.x) / denom;
  if (t < -0.0001 || t > 1.0001 || u < -0.0001 || u > 1.0001) return null;
  return { point: { x: a.x + t * r.x, y: a.y + t * r.y }, t, u };
}

function nearestSegment(point, segments) {
  let best = null;
  segments.forEach((segment, index) => {
    const projected = projectPointToSegment(point, segment.a, segment.b);
    if (!best || projected.distance < best.distance) best = { ...projected, segmentIndex: index };
  });
  return best;
}

function corridorConnectionsToShape(corridors, shape, imgW = 1000, imgH = 700) {
  if (!corridors.length || !shape) return [];
  const roomEdges = shapeBoundarySegments(shape, imgW, imgH);
  if (!roomEdges.length) return [];
  const candidates = [];

  const addCandidate = (
    corridorPoint,
    roomPoint,
    distance,
    segmentKey
  ) => {
    if (
      !corridorPoint ||
      !roomPoint ||
      !Number.isFinite(distance)
    ) {
      return;
    }
    candidates.push({ corridorPoint, roomPoint, distance, segmentKey });
  };

  for (let corridorIndex = 0; corridorIndex < corridors.length; corridorIndex += 1) {
    const corridor = corridors[corridorIndex];
    for (let i = 0; i < corridor.points.length - 1; i += 1) {
      const corridorA = corridor.points[i];
      const corridorB = corridor.points[i + 1];
      const segmentKey = `${corridorIndex}:${i}`;
      for (const edge of roomEdges) {
        const intersection = segmentIntersection(
          corridorA,
          corridorB,
          edge.a,
          edge.b
        );
        if (intersection) {
          addCandidate(
            intersection.point,
            intersection.point,
            0,
            segmentKey
          );
          continue;
        }

        const fromRoomA = projectPointToSegment(edge.a, corridorA, corridorB);
        const fromRoomB = projectPointToSegment(edge.b, corridorA, corridorB);
        const fromCorridorA = projectPointToSegment(corridorA, edge.a, edge.b);
        const fromCorridorB = projectPointToSegment(corridorB, edge.a, edge.b);
        addCandidate(fromRoomA.point, edge.a, fromRoomA.distance, segmentKey);
        addCandidate(fromRoomB.point, edge.b, fromRoomB.distance, segmentKey);
        addCandidate(corridorA, fromCorridorA.point, fromCorridorA.distance, segmentKey);
        addCandidate(corridorB, fromCorridorB.point, fromCorridorB.distance, segmentKey);
      }
    }
  }

  const closestBySegment = new Map();
  candidates.forEach((candidate) => {
    const previous = closestBySegment.get(candidate.segmentKey);
    if (!previous || candidate.distance < previous.distance) {
      closestBySegment.set(candidate.segmentKey, candidate);
    }
  });

  return Array.from(closestBySegment.values()).sort(
    (a, b) => a.distance - b.distance
  );
}

function shortestCorridorPath(corridors, start, end, closures = [], imgW = 1000, imgH = 700) {
  if (!corridors.length || !start || !end) return [];
  const segments = [];
  const corridorEndpoints = [];
  corridors.forEach((corridor, corridorIndex) => {
    corridorEndpoints.push(corridor.points[0], corridor.points[corridor.points.length - 1]);
    for (let i = 0; i < corridor.points.length - 1; i += 1) {
      segments.push({
        a: corridor.points[i], b: corridor.points[i + 1],
        corridorIndex, pointIndex: i,
        splits: [{ point: corridor.points[i], t: 0 }, { point: corridor.points[i + 1], t: 1 }],
      });
    }
  });
  if (!segments.length) return [];
  const startProjection = nearestSegment(start, segments);
  const endProjection = nearestSegment(end, segments);
  if (!startProjection || !endProjection) return [];
  segments[startProjection.segmentIndex].splits.push({ point: startProjection.point, t: startProjection.t });
  segments[endProjection.segmentIndex].splits.push({ point: endProjection.point, t: endProjection.t });
  corridorEndpoints.forEach((endpoint) => {
    segments.forEach((segment) => {
      if (distanceBetween(endpoint, segment.a) < 0.5 || distanceBetween(endpoint, segment.b) < 0.5) return;
      const projected = projectPointToSegment(endpoint, segment.a, segment.b);
      if (projected.distance <= WALKWAY_SNAP_THRESHOLD) segment.splits.push({ point: projected.point, t: projected.t });
    });
  });
  for (let i = 0; i < segments.length; i += 1) {
    for (let j = i + 1; j < segments.length; j += 1) {
      const hit = segmentIntersection(segments[i].a, segments[i].b, segments[j].a, segments[j].b);
      if (hit) {
        segments[i].splits.push({ point: hit.point, t: hit.t });
        segments[j].splits.push({ point: hit.point, t: hit.u });
      }
    }
  }
  const nodes = new Map();
  const edges = new Map();
  const getNodeId = (point) => {
    const key = pointKey(point);
    if (!nodes.has(key)) { nodes.set(key, { id: key, x: point.x, y: point.y }); edges.set(key, []); }
    return key;
  };
  const addEdge = (from, to) => {
    if (from === to) return;
    const a = nodes.get(from), b = nodes.get(to);
    if (isSegmentBlocked(a, b, closures, imgW, imgH)) return;
    const weight = distanceBetween(a, b);
    edges.get(from).push({ to, weight });
    edges.get(to).push({ to: from, weight });
  };
  segments.forEach((segment) => {
    const unique = Array.from(
      new Map(
        segment.splits
          .filter((split) => split.t >= -0.0001 && split.t <= 1.0001)
          .map((split) => [pointKey(split.point), split])
      ).values()
    ).sort((a, b) => a.t - b.t);
    for (let i = 0; i < unique.length - 1; i += 1) addEdge(getNodeId(unique[i].point), getNodeId(unique[i + 1].point));
  });
  const nodeList = Array.from(nodes.values());
  const endpointIds = new Set(corridorEndpoints.map((point) => pointKey(point)));
  endpointIds.forEach((endpointId) => {
    const endpoint = nodes.get(endpointId);
    if (!endpoint) return;
    nodeList.forEach((node) => {
      if (node.id === endpointId) return;
      if (distanceBetween(endpoint, node) <= WALKWAY_SNAP_THRESHOLD) addEdge(endpointId, node.id);
    });
  });
  const startId = getNodeId(startProjection.point);
  const endId = getNodeId(endProjection.point);
  const distances = new Map([[startId, 0]]);
  const previous = new Map();
  const queue = new Set(nodes.keys());
  while (queue.size) {
    let current = null, bestDistance = Infinity;
    queue.forEach((id) => { const v = distances.get(id) ?? Infinity; if (v < bestDistance) { bestDistance = v; current = id; } });
    if (!current || current === endId) break;
    queue.delete(current);
    edges.get(current).forEach((edge) => {
      if (!queue.has(edge.to)) return;
      const nextDistance = bestDistance + edge.weight;
      if (nextDistance < (distances.get(edge.to) ?? Infinity)) { distances.set(edge.to, nextDistance); previous.set(edge.to, current); }
    });
  }
  if (startId !== endId && !previous.has(endId)) {
    // Never invent a shortcut between disconnected corridors. A missing path
    // should be reported so the corridor can be fixed in the admin map.
    return [];
  }

  const path = [];
  let cursor = endId;
  while (cursor) {
    path.unshift(nodes.get(cursor));
    if (cursor === startId) break;
    cursor = previous.get(cursor);
  }
  const corridorEnd = endProjection.point;
  return [
    start,
    ...path.filter((point) => distanceBetween(point, start) > 0.5 && distanceBetween(point, corridorEnd) > 0.5),
    corridorEnd,
  ];
}

export default function UserNavigation() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    heading: deviceHeading,
    supported: headingSupported,
    requestingPermission: requestingHeadingPermission,
    error: headingError,
    requestPermission: requestHeadingPermission,
  } = useDeviceHeading();

  const floorId = searchParams.get("floorId");
  const { t, i18n } = useTranslation();
  const targetFloorId = searchParams.get("targetFloorId") || floorId;
  const buildingId = searchParams.get("buildingId");
  const fromQrId = searchParams.get("fromQrId");
  const toId = searchParams.get("toId");
  const routeMode = searchParams.get("routeMode") === "stair" ? "stair" : "elevator";
  const destName = searchParams.get("destName") || t("user.navigation.destination", "ปลายทาง");
  const start = useMemo(() => readPoint(searchParams, "start"), [searchParams]);
  const end = useMemo(() => readPoint(searchParams, "end"), [searchParams]);

  // ===== Cross-building (ทางเชื่อม) =====
  // targetBuildingId: ตึกปลายทาง (มาจาก UserLayout) — ถ้าต่างจาก buildingId แปลว่าต้องข้ามตึก
  const targetBuildingId = searchParams.get("targetBuildingId");
  // xb* = ข้อมูลปลายทางจริง ที่จะนำทางต่อหลังข้ามทางเชื่อม (Phase 2)
  const xbToId = searchParams.get("xbToId"); // มีค่า = กำลังอยู่ Phase 1 (เดินไปทางเชื่อม)
  const isCrossBuildingLeg = !!xbToId;
  const needsBridgeResolve = !!targetBuildingId && String(targetBuildingId) !== String(buildingId) && !xbToId;
  const usedBridgeNamesParam = searchParams.get("usedBridgeNames") || "";

  const [floorData, setFloorData] = useState(null);
  const [targetFloorData, setTargetFloorData] = useState(null);
  const [shapes, setShapes] = useState([]);
  const [targetShapes, setTargetShapes] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [targetDestinations, setTargetDestinations] = useState([]);
  const [corridors, setCorridors] = useState([]);
  const [corridorsLoaded, setCorridorsLoaded] = useState(false);
  const [closures, setClosures] = useState([]);
  const [targetClosures, setTargetClosures] = useState([]);
  const [routePath, setRoutePath] = useState([]);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transfer, setTransfer] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepCardOpen, setStepCardOpen] = useState(true);
  const [navigationStarted, setNavigationStarted] = useState(false);

  // reset เมื่อมีเส้นทางใหม่
  useEffect(() => {
    setCurrentStepIndex(0);
    setStepCardOpen(true);
  }, [steps]);

  const configuredNorthOffset = Number(
    floorData?.mapNorthOffset ??
      floorData?.northOffset ??
      floorData?.northRotation ??
      0
  );
  const mapNorthOffset = Number.isFinite(configuredNorthOffset)
    ? configuredNorthOffset
    : 0;
  const arrowHeading = Number.isFinite(deviceHeading)
    ? normalizeDegrees(deviceHeading - (Number.isFinite(mapNorthOffset) ? mapNorthOffset : 0))
    : null;

  const startNavigation = useCallback(async () => {
    if (!headingSupported) {
      setNavigationStarted(true);
      return;
    }
    const allowed = await requestHeadingPermission();
    if (allowed) setNavigationStarted(true);
  }, [headingSupported, requestHeadingPermission]);

  const onRescanResolved = ({ qr, floor, building }) => {
    setScannerOpen(false);
    if (!qr) return;
    const nextBuildingId = building?.id || qr.buildingId || buildingId;
    const nextFloorId = floor?.id || qr.floorId || floorId;
    const sx = Math.round(Number(qr.x) || 0);
    const sy = Math.round(Number(qr.y) || 0);
    const nextParams = new URLSearchParams(searchParams);
    if (nextBuildingId) nextParams.set("buildingId", String(nextBuildingId));
    nextParams.set("floorId", String(nextFloorId));
    nextParams.set("fromQrId", String(qr.id));
    nextParams.set("startX", String(sx));
    nextParams.set("startY", String(sy));
    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    if (!floorId) { setFloorData(null); setShapes([]); setDestinations([]); setCorridors([]); setCorridorsLoaded(false); return; }
    setShapes([]); setDestinations([]); setCorridors([]); setCorridorsLoaded(false);
    loadFloorNavigationData(floorId).then(setFloorData).catch(() => setFloorData(null));
    ShapeAPI.listByFloor(floorId)
      .then((data) => setShapes((Array.isArray(data) ? data : []).filter((shape) => shapeBelongsToFloor(shape, floorId))))
      .catch(() => setShapes([]));
    DestinationAPI.listByFloor(floorId)
      .then((d) => setDestinations(Array.isArray(d) ? d : []))
      .catch(() => setDestinations([]));
    NavigationGraphAPI.listCorridors(floorId)
      .then((data) => setCorridors(normalizeCorridors(data)))
      .catch(() => setCorridors([]))
      .finally(() => setCorridorsLoaded(true));
    setClosures([]);
    PathClosureAPI.listByFloor(floorId, "active")
      .then((d) => setClosures(Array.isArray(d) ? d : d?.items || []))
      .catch(() => setClosures([]));
  }, [floorId]);

  useEffect(() => {
    if (!targetFloorId || String(targetFloorId) === String(floorId)) { setTargetFloorData(null); setTargetShapes([]); setTargetDestinations([]); return; }
    loadFloorNavigationData(targetFloorId).then(setTargetFloorData).catch(() => setTargetFloorData(null));
    ShapeAPI.listByFloor(targetFloorId)
      .then((data) => setTargetShapes((Array.isArray(data) ? data : []).filter((shape) => shapeBelongsToFloor(shape, targetFloorId))))
      .catch(() => setTargetShapes([]));
    DestinationAPI.listByFloor(targetFloorId)
      .then((d) => setTargetDestinations(Array.isArray(d) ? d : []))
      .catch(() => setTargetDestinations([]));
    setTargetClosures([]);
    PathClosureAPI.listByFloor(targetFloorId, "active")
      .then((d) => setTargetClosures(Array.isArray(d) ? d : d?.items || []))
      .catch(() => setTargetClosures([]));
  }, [floorId, targetFloorId]);

  // ===== Cross-building bridge resolver (Phase 0 → Phase 1) =====
  // เมื่อปลายทางอยู่คนละตึก: หาทางเชื่อม (bridge) ที่ชื่อตรงกันระหว่าง 2 ตึก
  // แล้วเขียน URL ใหม่ให้ "ปลายทางชั่วคราว" = ทางเชื่อมในตึกปัจจุบัน + เก็บปลายทางจริงไว้ใน xb*
  useEffect(() => {
    if (!needsBridgeResolve) return;
    if (!floorData) { setLoading(true); setError(""); return; }
    let cancelled = false;

    async function resolveBridge() {
      setLoading(true);
      setError("");
      try {
        // โหลด bridges ของตึกหนึ่ง (ทุกชั้น)
        const loadBridgesOfBuilding = async (bId) => {
          const floorList = await FloorAPI.listByBuilding(bId)
            .then((d) => (Array.isArray(d) ? d : d?.items || []))
            .catch(() => []);
          const perFloor = await Promise.all(
            floorList.map(async (floor) => {
              const [shapeData, fData] = await Promise.all([
                ShapeAPI.listByFloor(floor.id).catch(() => []),
                loadFloorNavigationData(floor.id).catch(() => null),
              ]);
              const shapeList = (Array.isArray(shapeData) ? shapeData : []).filter((s) =>
                shapeBelongsToFloor(s, floor.id)
              );
              return getFloorBridges(fData, shapeList).map((b) => ({
                ...b,
                floorId: floor.id,
                floorLevel: fData?.level ?? fData?.floorLevel ?? floor.level,
                floorName: fData?.name || floor.name || `${t("user.layout.floor")} ${floor.level || ""}`,
              }));
            })
          );
          return perFloor.flat();
        };

        const buildingList = await loadAllBuildingsForBridgeGraph().catch(() => []);
        const bridgeBuildingIds = Array.from(
          new Set([
            ...buildingList.map((building) => String(building.id)),
            String(buildingId),
            String(targetBuildingId),
          ])
        );
        const bridgeEntries = await Promise.all(
          bridgeBuildingIds.map(async (bId) => [bId, await loadBridgesOfBuilding(bId)])
        );
        const bridgesByBuilding = new Map(bridgeEntries);

        if (cancelled) return;

        const usedBridgeNames = new Set(usedBridgeNamesParam.split(",").filter(Boolean));
        const nextHop = findBridgeRouteHop(
          bridgesByBuilding,
          buildingId,
          targetBuildingId,
          start,
          floorId,
          floorData,
          usedBridgeNames
        );
        const nextBuildingId = nextHop?.to || targetBuildingId;
        const isFinalBridgeHop = String(nextBuildingId) === String(targetBuildingId);
        const matchedCurrent = nextHop?.currentBridge || null;
        const matchedTarget = nextHop?.targetBridge || null;
        const nextUsedBridgeNames = Array.from(new Set([...usedBridgeNames, nextHop?.name].filter(Boolean)));

        if (!matchedCurrent || !matchedTarget) {
          setError(
            t(
              "user.navigation.noBridgeMatch",
              "ไม่พบทางเชื่อมที่ชื่อตรงกันระหว่างสองอาคาร กรุณาตรวจสอบชื่อทางเชื่อมในหน้าแอดมิน"
            )
          );
          setLoading(false);
          return;
        }

        const startIsAlreadyAtBridge =
          start &&
          String(floorId) === String(matchedCurrent.floorId) &&
          distanceBetween(start, matchedCurrent) <= BRIDGE_ARRIVAL_THRESHOLD;

        if (startIsAlreadyAtBridge) {
          const nextRoute = isFinalBridgeHop
            ? {
                buildingId: String(nextBuildingId),
                floorId: String(matchedTarget.floorId),
                targetFloorId: String(targetFloorId),
                toId: String(toId || ""),
                startX: String(Math.round(Number(matchedTarget.x) || 0)),
                startY: String(Math.round(Number(matchedTarget.y) || 0)),
                endX: String(Math.round(end?.x || 0)),
                endY: String(Math.round(end?.y || 0)),
                destName,
                routeMode,
                usedBridgeNames: nextUsedBridgeNames.join(","),
              }
            : {
                buildingId: String(nextBuildingId),
                floorId: String(matchedTarget.floorId),
                targetFloorId: String(targetFloorId),
                targetBuildingId: String(targetBuildingId),
                toId: String(toId || ""),
                startX: String(Math.round(Number(matchedTarget.x) || 0)),
                startY: String(Math.round(Number(matchedTarget.y) || 0)),
                endX: String(Math.round(end?.x || 0)),
                endY: String(Math.round(end?.y || 0)),
                destName,
                routeMode,
                usedBridgeNames: nextUsedBridgeNames.join(","),
              };
          setSearchParams(new URLSearchParams(nextRoute), { replace: true });
          return;
        }

        // เขียน URL ใหม่: Phase 1 → นำทางไปทางเชื่อมในตึกปัจจุบัน
        const next = new URLSearchParams(searchParams);
        next.delete("targetBuildingId");
        next.set("targetFloorId", String(matchedCurrent.floorId));
        next.set("toId", String(matchedCurrent.shapeId));
        next.set("endX", String(Math.round(Number(matchedCurrent.x) || 0)));
        next.set("endY", String(Math.round(Number(matchedCurrent.y) || 0)));
        next.set(
          "destName",
          t("user.navigation.bridgeDestName", {
            bridge: translateText(matchedCurrent.name) || translateText("ทางเชื่อม"),
            defaultValue: "{{bridge}} (ทางเชื่อม)",
          })
        );
        // เก็บปลายทางจริงไว้ใน xbFinal* และเก็บตึกถัดไปไว้ใน xb* สำหรับ hop นี้
        next.set("xbToId", String(toId || ""));
        next.set("xbBuildingId", String(nextBuildingId));
        next.set("xbFloorId", String(isFinalBridgeHop ? targetFloorId : matchedTarget.floorId));
        next.set("xbEndX", String(Math.round(end?.x || 0)));
        next.set("xbEndY", String(Math.round(end?.y || 0)));
        next.set("xbDestName", destName);
        next.set("xbFinalBuildingId", String(targetBuildingId));
        next.set("xbFinalFloorId", String(targetFloorId));
        next.set("xbFinalToId", String(toId || ""));
        next.set("xbFinalEndX", String(Math.round(end?.x || 0)));
        next.set("xbFinalEndY", String(Math.round(end?.y || 0)));
        next.set("xbFinalDestName", destName);
        next.set("xbIsFinalHop", isFinalBridgeHop ? "1" : "0");
        next.set("xbBridgeFloorId", String(matchedTarget.floorId));
        next.set("xbBridgeX", String(Math.round(Number(matchedTarget.x) || 0)));
        next.set("xbBridgeY", String(Math.round(Number(matchedTarget.y) || 0)));
        next.set("xbBridgeName", String(matchedTarget.name || ""));
        next.set("usedBridgeNames", nextUsedBridgeNames.join(","));

        setSearchParams(next, { replace: true });
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || t("user.navigation.cannotCalculate"));
          setLoading(false);
        }
      }
    }

    resolveBridge();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsBridgeResolve, buildingId, targetBuildingId, usedBridgeNamesParam, floorData]);

  useEffect(() => {
    let cancelled = false;
    async function loadPath() {
      // กำลังหาทางเชื่อมข้ามตึกอยู่ → รอ resolveBridge เขียน URL ก่อน
      if (needsBridgeResolve) { setLoading(true); setError(""); return; }
      if (!floorId || !start || !end) { setRoutePath([]); setSteps([]); setError(t("user.navigation.noStartEnd")); return; }
      if (!corridorsLoaded) { setLoading(true); setError(""); return; }
      if (String(targetFloorId) !== String(floorId) && !targetFloorData) { setLoading(true); setError(""); return; }
      setLoading(true); setError(""); setTransfer(null);
      try {
        const imgWcur = floorData?.imageWidth || 1000;
        const imgHcur = floorData?.imageHeight || 700;
        const requiresVerticalTransfer = isDifferentPhysicalFloor(
          floorData,
          targetFloorData,
          floorId,
          targetFloorId
        );
        if (requiresVerticalTransfer) {
          const bestTransfer = findBestConnectorTransfer(
            corridors,
            start,
            floorData,
            targetFloorData,
            shapes,
            targetShapes,
            routeMode,
            closures
          );
          if (!bestTransfer) {
            setRoutePath([]); setSteps([]);
            setError(t("user.navigation.noElevatorTransfer", "ไม่พบลิฟต์/บันไดที่เชื่อมระหว่างชั้นนี้กับชั้นปลายทาง"));
            setLoading(false); return;
          }
          if (isPathBlocked(bestTransfer.path, closures, imgWcur, imgHcur)) {
            setRoutePath([]); setSteps([]);
            setError(t("user.navigation.noCorridorPath"));
            setLoading(false); return;
          }
          setRoutePath(bestTransfer.path);
          setSteps([
            ...buildTurnSteps(bestTransfer.path, t, { includeArrival: false, scale: floorData?.metersPerPixel || 0.05 }),
            {
              type: "transfer",
              pointIndex: bestTransfer.path.length - 1,
              point: bestTransfer.path[bestTransfer.path.length - 1],
              text:
                routeMode === "stair"
                  ? t("user.navigation.takeStairs", {
                    stair: bestTransfer.connectorName || t("user.layout.stairs"),
                    floor: targetFloorData?.name || targetFloorId,
                    defaultValue: "ใช้ {{stair}} ไปยัง {{floor}}",
                  })
                  : t("user.navigation.takeElevator", {
                    elevator: bestTransfer.connectorName || t("user.layout.elevator"),
                    floor: targetFloorData?.name || targetFloorId,
                  }),
            },
          ]);
          setTransfer({
            connectorType: routeMode,
            elevatorName: bestTransfer.connectorName,
            currentElevator: bestTransfer.currentConnector,
            targetElevator: bestTransfer.targetConnector,
          });
          setLoading(false);
          return;
        }
        const imgW = floorData?.imageWidth || 1000;
        const imgH = floorData?.imageHeight || 700;
        const destinationShape =
          shapes.find((shape) => String(shape.id) === String(toId)) ||
          targetShapes.find((shape) => String(shape.id) === String(toId));
        const connections = destinationShape
          ? corridorConnectionsToShape(corridors, destinationShape, imgW, imgH)
          : [{ corridorPoint: end, roomPoint: end, distance: 0 }];
        const reachableConnections = connections
          .slice(0, 32)
          .map((connection) => {
            const corridorPath = shortestCorridorPath(
              corridors,
              start,
              connection.corridorPoint,
              closures,
              imgW,
              imgH
            );
            if (corridorPath.length < 2) return null;
            const roomApproachBlocked = isSegmentBlocked(
              connection.corridorPoint,
              connection.roomPoint,
              closures,
              imgW,
              imgH
            );
            if (roomApproachBlocked) return null;
            const finalPath =
              distanceBetween(
                corridorPath[corridorPath.length - 1],
                connection.roomPoint
              ) > 1
                ? [...corridorPath, connection.roomPoint]
                : corridorPath;
            return {
              path: finalPath,
              approachDistance: connection.distance,
              corridorDistance: routeDistance(corridorPath),
            };
          })
          .filter(Boolean)
          .sort(
            (a, b) =>
              a.approachDistance - b.approachDistance ||
              a.corridorDistance - b.corridorDistance
          );
        const bestRoute = reachableConnections[0];

        if (!bestRoute) {
          setRoutePath([]);
          setSteps([]);
          setError(t("user.navigation.noCorridorPath"));
          setLoading(false);
          return;
        }
        setRoutePath(bestRoute.path);
        setSteps(buildTurnSteps(bestRoute.path, t, { scale: floorData?.metersPerPixel || 0.05 }));
        setLoading(false);
        return;
      } catch (err) {
        if (!cancelled) { setRoutePath([]); setSteps([]); setError(err?.message || t("user.navigation.cannotCalculate")); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadPath();

    return () => {
      cancelled = true;
    };
  }, [floorId, targetFloorId, start, end, corridors, corridorsLoaded, closures, targetClosures, floorData, targetFloorData, shapes, targetShapes, toId, routeMode, needsBridgeResolve, t]);

  const imageUrl = resolveAsset(floorData?.imageUrl);
  const safeStepIndex = steps.length ? Math.min(currentStepIndex, steps.length - 1) : 0;
  const currentStep = steps[safeStepIndex] || null;
  const isArrivalStep = currentStep?.type === "arrive" && !transfer;

  const destinationDetail = useMemo(() => {
    const shape = shapes.find((item) => String(item.id) === String(toId)) ||
      targetShapes.find((item) => String(item.id) === String(toId));

    let linkedDest = null;
    const allDests = [...destinations, ...targetDestinations];
    if (shape?.destinationId) {
      linkedDest = allDests.find((d) => String(d.id) === String(shape.destinationId));
    } else if (shape) {
      linkedDest = allDests.find((d) => d.name === shape.name);
    }

    const name = linkedDest?.name || shape?.name || destName;
    const description = linkedDest?.description || shape?.description || shape?.detail || "";
    const rawImg = linkedDest?.image || linkedDest?.imageUrl || linkedDest?.img || shape?.imageUrl || shape?.image || shape?.img || "";

    return {
      name,
      description,
      category: linkedDest?.category || shape?.category || "",
      image: resolveAsset(rawImg) || null,
    };
  }, [shapes, targetShapes, destinations, targetDestinations, toId, destName]);

  const routeStepMarker = useMemo(() => {
    if (!steps.length) return null;
    const safeIndex = Math.min(currentStepIndex, steps.length - 1);
    const current = steps[safeIndex];
    const point = current?.point;
    if (!point) return null;

    const nextPoint = steps[safeIndex + 1]?.point;
    const previousPoint = steps[safeIndex - 1]?.point;
    const directionPoint = nextPoint || previousPoint;
    const dx = directionPoint ? directionPoint.x - point.x : 0;
    const dy = directionPoint ? directionPoint.y - point.y : 0;
    const routeRotation = directionPoint && (dx !== 0 || dy !== 0)
      ? (Math.atan2(dx, -dy) * 180) / Math.PI
      : 0;

    const heading =
      navigationStarted &&
      Number.isFinite(arrowHeading)
        ? arrowHeading
        : routeRotation;

    return {
      x: point.x,
      y: point.y,
      rotation: heading,
      type: current.type,
    };
  }, [
    steps,
    currentStepIndex,
    arrowHeading,
    navigationStarted,
  ]);

  const destinationMarkers = useMemo(() => {
    const imgW = floorData?.imageWidth || 1000;
    const imgH = floorData?.imageHeight || 700;
    return shapes
      .filter((shape) => !shape.hidden && shape.name)
      .filter((shape) => String(shape.id) === String(toId)) // เฉพาะปลายทางที่เลือก
      .map((shape) => {
        const center = getShapeCenter(shape, imgW, imgH);

        let linkedDest = null;
        if (shape.destinationId) {
          linkedDest = destinations.find((d) => String(d.id) === String(shape.destinationId));
        } else {
          linkedDest = destinations.find((d) => d.name === shape.name);
        }

        const name = linkedDest?.name || shape.name;
        const category = linkedDest?.category || shape.category || "room";
        const rawImg = linkedDest?.image || linkedDest?.imageUrl || linkedDest?.img || shape.imageUrl || shape.image || shape.img || "";

        let markerType = category;
        const text = `${category} ${name}`.toLowerCase();
        const isRestroom = text.includes("restroom") || text.includes("toilet") || text.includes("bathroom") || text.includes("wc") || text.includes("ห้องน้ำ");
        const isStair = category === "stair" || category === "stairs";
        const isElevator = category === "elevator";

        if (isRestroom) markerType = "bathroom";
        else if (isStair) markerType = "stair";
        else if (isElevator) markerType = "elevator";

        return {
          id: `shape-${shape.id}`,
          type: markerType,
          x: Math.round(center.x),
          y: Math.round(center.y),
          label: parseNames(name).primary,
          imageUrl: resolveAsset(rawImg) || null,
          permanent: true,
          showLabel: true,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorData, shapes, destinations, toId, i18n.language]);

  const markers = useMemo(() => {
    const out = [];

    // 1. ต้นทาง (Start QR Point)
    if (fromQrId) {
      const startQr = (floorData?.qr || []).find((point) => String(point.id) === String(fromQrId));
      if (startQr) {
        out.push({
          id: `qr-start-${startQr.id}`,
          type: "qr",
          x: startQr.x,
          y: startQr.y,
          label: t("user.navigation.startPoint") || "จุดเริ่มต้น",
          permanent: true,
        });
      }
    }

    // 2. ลิฟต์ หรือ บันได หากต้องเปลี่ยนชั้น (Transfer Connector)
    if (transfer?.currentElevator) {
      const isStair = transfer.connectorType === "stair" || transfer.connectorType === "stairs";
      out.push({
        id: "transfer-connector",
        type: isStair ? "stair" : "elevator",
        x: Number(transfer.currentElevator.x) || 0,
        y: Number(transfer.currentElevator.y) || 0,
        label: isStair
          ? translateText(transfer.elevatorName) || t("user.layout.stairs") || "บันได"
          : translateText(transfer.elevatorName) || t("user.layout.elevator") || "ลิฟต์",
        permanent: true,
      });
    }

    // 3. ปลายทาง (Destination Markers)
    return [...destinationMarkers, ...out];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destinationMarkers, floorData, fromQrId, transfer, t, i18n.language]);

  const continueOnTargetFloor = () => {
    if (!transfer?.targetElevator) return;
    // คง xb* (cross-building) ไว้ ถ้ากำลังเดินไปทางเชื่อม
    const params = new URLSearchParams(searchParams);
    if (buildingId) params.set("buildingId", String(buildingId));
    params.set("floorId", String(targetFloorId));
    params.set("targetFloorId", String(targetFloorId));
    params.set("fromQrId", fromQrId || "");
    params.set("toId", String(toId || ""));
    params.set("startX", String(Math.round(Number(transfer.targetElevator.x) || 0)));
    params.set("startY", String(Math.round(Number(transfer.targetElevator.y) || 0)));
    params.set("endX", String(Math.round(end?.x || 0)));
    params.set("endY", String(Math.round(end?.y || 0)));
    params.set("destName", destName);
    params.set("routeMode", routeMode);
    navigate(`/user/usernavigation?${params.toString()}`, { replace: true });
  };

  // Phase 1 → next hop: ข้ามทางเชื่อมไปตึกถัดไป แล้วไปต่อจนถึงตึกปลายทางจริง
  const crossBridgeToTarget = () => {
    const xbBuildingId = searchParams.get("xbBuildingId");
    const xbFloorId = searchParams.get("xbFloorId");
    const xbBridgeFloorId = searchParams.get("xbBridgeFloorId");
    const xbBridgeX = searchParams.get("xbBridgeX");
    const xbBridgeY = searchParams.get("xbBridgeY");
    const xbEndX = searchParams.get("xbEndX");
    const xbEndY = searchParams.get("xbEndY");
    const xbToId = searchParams.get("xbToId");
    const xbDestName = searchParams.get("xbDestName");
    const xbFinalBuildingId = searchParams.get("xbFinalBuildingId") || xbBuildingId;
    const xbFinalFloorId = searchParams.get("xbFinalFloorId") || xbFloorId || xbBridgeFloorId;
    const xbFinalToId = searchParams.get("xbFinalToId") || xbToId || "";
    const xbFinalEndX = searchParams.get("xbFinalEndX") || xbEndX;
    const xbFinalEndY = searchParams.get("xbFinalEndY") || xbEndY;
    const xbFinalDestName = searchParams.get("xbFinalDestName") || xbDestName || destName;
    const isFinalBridgeHop = searchParams.get("xbIsFinalHop") !== "0";
    const usedBridgeNames = searchParams.get("usedBridgeNames") || "";
    if (!xbBuildingId || !xbBridgeFloorId) return;

    const nextRoute = isFinalBridgeHop
      ? {
          buildingId: String(xbBuildingId),
          floorId: String(xbBridgeFloorId),
          targetFloorId: String(xbFloorId || xbBridgeFloorId),
          toId: String(xbToId || ""),
          startX: String(Math.round(Number(xbBridgeX) || 0)),
          startY: String(Math.round(Number(xbBridgeY) || 0)),
          endX: String(Math.round(Number(xbEndX) || 0)),
          endY: String(Math.round(Number(xbEndY) || 0)),
          destName: xbDestName || destName,
          routeMode,
          usedBridgeNames,
        }
      : {
          buildingId: String(xbBuildingId),
          floorId: String(xbBridgeFloorId),
          targetFloorId: String(xbFinalFloorId),
          targetBuildingId: String(xbFinalBuildingId),
          toId: String(xbFinalToId),
          startX: String(Math.round(Number(xbBridgeX) || 0)),
          startY: String(Math.round(Number(xbBridgeY) || 0)),
          endX: String(Math.round(Number(xbFinalEndX) || 0)),
          endY: String(Math.round(Number(xbFinalEndY) || 0)),
          destName: xbFinalDestName,
          routeMode,
          usedBridgeNames,
        };
    const params = new URLSearchParams(nextRoute);
    navigate(`/user/usernavigation?${params.toString()}`, { replace: true });
  };

  const scanOnTargetFloor = () => {
    const params = new URLSearchParams({
      ...(buildingId ? { buildingId } : {}),
      floorId: String(targetFloorId),
      destId: String(toId || ""),
      destFloorId: String(targetFloorId),
      routeMode,
    });
    navigate(`/user/mainpage?${params.toString()}`, { replace: false });
  };

  return (
    <div className="relative h-full w-full overflow-hidden bg-slate-100">
      <div className="absolute inset-0 pb-[132px]">
        <LeafletFloorMap
          imageUrl={imageUrl}
          imageWidth={floorData?.imageWidth}
          imageHeight={floorData?.imageHeight}
          shapes={shapes}
          markers={markers}
          routePath={routePath}
          routeStepMarker={routeStepMarker}
          focusPoint={routeStepMarker || start}
          focusKey={`${floorId || ""}-${fromQrId || ""}-${toId || ""}-${currentStepIndex}-${start?.x || ""}-${start?.y || ""}`}
          focusZoomOffset={-0.5}
          className="h-full w-full"
          currentStepIndex={currentStepIndex}
          showShapes={false}
        />
      </div>

      <button
        onClick={() => navigate(-1)}
        className="fixed left-3 top-3 z-[1100] grid h-11 w-11 place-items-center rounded-xl bg-white text-2xl font-bold text-slate-600 shadow"
        aria-label="ย้อนกลับ"
      >
        ‹
      </button>
      {!navigationStarted && (
        <div className="fixed inset-0 z-[1050] grid place-items-center bg-slate-950/45 px-6 backdrop-blur-[2px]">
          <div className="w-full max-w-xs rounded-3xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-blue-100 text-5xl font-black text-blue-600">
              ↑
            </div>
            <div className="mt-4 text-xl font-black text-slate-900">
              {t("user.navigation.readyToStart", "พร้อมเริ่มนำทาง")}
            </div>
            <div className="mt-2 text-sm font-semibold leading-6 text-slate-500">
              {headingSupported
                ? t(
                    "user.navigation.directionPermissionDesc",
                    "กดเริ่มเพื่ออนุญาตการใช้ทิศทางของโทรศัพท์"
                  )
                : headingError === "insecure"
                  ? "เซ็นเซอร์ทิศทางใช้ไม่ได้ผ่าน HTTP กรุณาเปิดเว็บไซต์ผ่าน HTTPS"
                : t(
                    "user.navigation.compassUnsupported",
                    "อุปกรณ์หรือเบราว์เซอร์นี้ไม่รองรับเข็มทิศ"
                  )}
            </div>
            {headingError === "denied" && (
              <div className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-xs font-bold text-red-600">
                {t(
                  "user.navigation.compassDenied",
                  "ไม่ได้รับอนุญาตให้ใช้เซ็นเซอร์ทิศทาง"
                )}
              </div>
            )}
            <button
              type="button"
              onClick={startNavigation}
              disabled={requestingHeadingPermission}
              className="mt-5 h-14 w-full rounded-2xl bg-blue-600 text-base font-black text-white shadow-lg shadow-blue-200 disabled:cursor-wait disabled:opacity-60"
            >
              {requestingHeadingPermission
                ? t("user.navigation.compassRequesting", "กำลังขอสิทธิ์...")
                : headingError === "denied"
                  ? t("user.navigation.tryAgain", "ลองอีกครั้ง")
                  : headingSupported
                    ? t("user.navigation.startNavigation", "เริ่มนำทาง")
                    : t("user.navigation.continueStepByStep", "นำทางแบบทีละขั้น")}
            </button>
            {headingError === "denied" && (
              <button
                type="button"
                onClick={() => setNavigationStarted(true)}
                className="mt-3 text-sm font-bold text-slate-500"
              >
                {t(
                  "user.navigation.continueWithoutCompass",
                  "ดำเนินการต่อโดยไม่ใช้ทิศทาง"
                )}
              </button>
            )}
          </div>
        </div>
      )}
      {scannerOpen && (
        <QrScannerModal onClose={() => setScannerOpen(false)} onResolved={onRescanResolved} />
      )}

      {isArrivalStep && (
        <>
          <div className="fixed bottom-0 left-0 right-0 z-[1100] rounded-t-2xl bg-white px-4 pb-5 pt-3 shadow-2xl">
            <div className="mx-auto mb-4 h-1 w-12 rounded-full bg-slate-300" />
            {(() => {
              const { primary, aliases } = parseNames(destinationDetail.name);
              return (
                <div className="text-center">
                  <div className="text-base font-black text-slate-900">
                    {primary}
                  </div>
                  {aliases.length > 0 && (
                    <div className="text-xs text-slate-500 font-normal mt-0.5">
                      {t("user.layout.otherNames", "ชื่ออื่น:")} {aliases.join(", ")}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="mt-4 overflow-hidden rounded-lg bg-slate-100">
              {destinationDetail.image ? (
                <img
                  src={destinationDetail.image}
                  alt=""
                  className="h-28 w-full object-cover"
                />
              ) : (
                <div className="flex h-28 items-center justify-center text-sm font-bold text-slate-400">
                  {parseNames(destinationDetail.name).primary}
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center justify-center gap-2 text-xs font-bold">
              <span className="max-w-[42%] truncate rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-blue-500">
                {t("user.navigation.buildingEntranceQr", "QR ทางเข้าตึก")}
              </span>
              <span className="text-blue-500">→</span>
              <span className="max-w-[42%] truncate rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-blue-500">
                {parseNames(destinationDetail.name).primary}
              </span>
            </div>

            <div className="mt-4 text-sm font-black text-slate-900">
              {t("user.menuList.description", "อธิบาย")}
            </div>
            <div className="mt-1 text-sm leading-6 text-slate-600">
              {isCrossBuildingLeg
                ? t(
                  "user.navigation.bridgeArrivalDescription",
                  "คุณมาถึงทางเชื่อมแล้ว เดินข้ามทางเชื่อมไปอีกอาคาร แล้วกดปุ่มด้านล่างเพื่อนำทางต่อ"
                )
                : destinationDetail.description ||
                t(
                  "user.navigation.arrivedTargetDesc",
                  "คุณมาถึงปลายทางแล้ว"
                )}
            </div>

            {isCrossBuildingLeg ? (
              <button
                onClick={crossBridgeToTarget}
                className="mt-4 h-11 w-full rounded-xl bg-blue-600 text-sm font-black text-white"
              >
                {t("user.navigation.crossBridgeAndContinue", {
                  building: searchParams.get("xbDestName") || destName,
                  defaultValue: "ข้ามทางเชื่อม แล้วนำทางต่อ ➜",
                })}
              </button>
            ) : (
              <button
                onClick={() => {
                  const params = new URLSearchParams();
                  if (buildingId) params.set("buildingId", String(buildingId));
                  if (floorId) params.set("floorId", String(floorId));
                  navigate(`/user/mainpage?${params.toString()}`, { replace: false });
                }}
                className="mt-4 h-11 w-full rounded-xl bg-emerald-500 text-sm font-black text-white"
              >
                {t("user.navigation.done", "เสร็จสิ้น")}
              </button>
            )}
          </div>
        </>
      )}

      {!isArrivalStep && (
        <div className="fixed bottom-0 left-0 right-0 z-[1000] rounded-t-2xl bg-white p-4 shadow-2xl">
          {stepCardOpen && steps.length > 0 && (() => {
            const safeIdx = Math.min(currentStepIndex, steps.length - 1);
            const cur = steps[safeIdx] || {};
            const isLast = safeIdx >= steps.length - 1;
            const isTransferStep = cur.type === "transfer";
            return (
              <div className="mb-3 rounded-2xl bg-slate-900 px-3 py-3 shadow-lg">
                <div className="flex items-start gap-3">
                  <div
                    className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl text-2xl font-black text-white ${cur.type === "arrive"
                        ? "bg-emerald-500"
                        : isTransferStep
                          ? "bg-amber-500"
                          : "bg-blue-500"
                      }`}
                  >
                    <StepCardIcon type={cur.type} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold text-slate-400">
                      {t("user.navigation.stepXofY", {
                        current: safeIdx + 1,
                        total: steps.length,
                        defaultValue: "ขั้นตอน {{current}} ถึง {{total}}",
                      })}
                    </div>
                    <div className="truncate text-base font-bold text-white">
                      {destName}
                    </div>
                    <div className="mt-0.5 truncate text-xs font-semibold text-slate-300">
                      {t("user.navigation.routeLabel", "เส้นทาง")} : {translateText(cur.text) || t("user.navigation.followPath")}
                    </div>
                    {isTransferStep && (
                      <div className="mt-1 text-xs font-semibold text-amber-200">
                        {t(
                          "user.navigation.confirmFloorInstruction",
                          "เมื่อถึงชั้นปลายทางแล้วให้กดยืนยัน หรือสแกน QR ก่อนเริ่มขั้นตอนถัดไป"
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => setStepCardOpen(false)}
                    className="text-slate-400 hover:text-white text-xl leading-none px-1"
                    aria-label="ปิด"
                  >
                    ×
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    onClick={() => setCurrentStepIndex((i) => Math.max(0, i - 1))}
                    disabled={safeIdx === 0}
                    className="flex-1 h-9 rounded-lg bg-slate-800 text-xs font-semibold text-slate-200 disabled:opacity-40"
                  >
                    ‹ {t("user.navigation.prevStep", "ก่อนหน้า")}
                  </button>
                  <button
                    onClick={() =>
                      setCurrentStepIndex((i) => Math.min(steps.length - 1, i + 1))
                    }
                    disabled={isLast || isTransferStep}
                    className="flex-1 h-9 rounded-lg bg-blue-600 text-xs font-semibold text-white disabled:opacity-40"
                  >
                    {isTransferStep
                      ? t("user.navigation.confirmFloorFirst", "ยืนยันชั้นก่อน")
                      : isLast
                        ? t("user.navigation.done", "เสร็จสิ้น")
                        : `${t("user.navigation.nextStep", "ถัดไป")} ›`}
                  </button>
                </div>
              </div>
            );
          })()}

          {!stepCardOpen && steps.length > 0 && (
            <button
              onClick={() => setStepCardOpen(true)}
              className="mb-3 w-full rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-200"
            >
              ↑ {t("user.navigation.showSteps", "แสดงขั้นตอนการนำทาง")}
            </button>
          )}

          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-xl bg-blue-50 px-3 py-2 text-center text-sm font-semibold text-blue-600">
              {loading
                ? t("user.navigation.calculating")
                : routePath.length > 1
                  ? t("user.navigation.readyToNavigate")
                  : t("user.navigation.noRoute")}
            </div>
            <button
              onClick={() => setScannerOpen(true)}
              className="flex shrink-0 items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
              aria-label={t("user.navigation.rescanQr", "สแกน QR เพื่ออัปเดตตำแหน่ง")}
            >
              <QrcodeIcon className="w-5 h-5 text-white" />
              {t("user.navigation.rescanQrShort", "สแกน QR")}
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600">
              {error}
            </div>
          )}

          {transfer && currentStep?.type === "transfer" && (
            <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50 p-3">
              <div className="text-sm font-bold text-blue-700">
                {routeMode === "stair"
                  ? t("user.navigation.stairTransferTitle", {
                    stair: transfer.elevatorName || t("user.layout.stairs"),
                    defaultValue: "ใช้{{stair}}ไปยังชั้นปลายทาง",
                  })
                  : t("user.navigation.elevatorTransferTitle", {
                    elevator: transfer.elevatorName || t("user.layout.elevator"),
                    defaultValue: "ขึ้น{{elevator}}ไปยังชั้นปลายทาง",
                  })}
              </div>
              <div className="mt-1 text-xs font-semibold leading-5 text-blue-600">
                {t("user.navigation.elevatorTransferDesc", {
                  elevator: translateText(transfer.elevatorName) || t(routeMode === "stair" ? "user.layout.stairs" : "user.layout.elevator"),
                  floor: translateText(targetFloorData?.name || targetFloorId),
                  defaultValue: "ใช้ {{elevator}} แล้วไปยัง {{floor}}",
                })}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  onClick={continueOnTargetFloor}
                  className="rounded-xl bg-blue-600 px-3 py-2 text-xs font-semibold text-white"
                >
                  {t("user.navigation.arrivedTargetFloor", "ถึงชั้นนี้แล้ว")}
                </button>
                <button
                  onClick={scanOnTargetFloor}
                  className="rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-600"
                >
                  {routeMode === "stair"
                    ? t("user.navigation.scanAtStair", "สแกน QR หน้าบันได")
                    : t("user.navigation.scanAtElevator", "สแกน QR หน้าลิฟต์")}
                </button>
              </div>
            </div>
          )}

          <button
            onClick={() => {
              const params = new URLSearchParams();
              if (buildingId) params.set("buildingId", String(buildingId));
              if (floorId) params.set("floorId", String(floorId));
              if (toId) params.set("destId", String(toId));
              if (targetFloorId) params.set("destFloorId", String(targetFloorId));
              navigate(`/user/mainpage?${params.toString()}`, { replace: false });
            }}
            className="mt-3 h-12 w-full rounded-xl bg-slate-900 font-semibold text-white"
          >
            {t("user.navigation.backToMap")}
          </button>
        </div>
      )}
    </div>
  );
}
